import type { QueryClient } from '@tanstack/react-query';
import {
  OPEN_STATUSES,
  type AdminRide,
  type DriverLocationEvent,
  type DriverUpdatedEvent,
  type OnlineDriver,
  type RideUpdatedEvent,
} from './types';

/** Query keys shared by pages and the realtime layer. */
export const qk = {
  dashboard: ['dashboard'] as const,
  liveRides: ['live', 'rides'] as const,
  liveDrivers: ['live', 'drivers'] as const,
  activeSos: ['sos', 'active'] as const,
  sos: ['sos'] as const,
  rides: ['rides'] as const,
  ride: (id: string) => ['ride', id] as const,
  drivers: ['drivers'] as const,
  driver: (id: string) => ['driver', id] as const,
  riders: ['riders'] as const,
  rider: (id: string) => ['rider', id] as const,
  fares: ['fares'] as const,
  settings: ['settings'] as const,
  staff: ['staff'] as const,
  audit: ['audit'] as const,
  settlements: ['settlements'] as const,
  ratings: ['ratings'] as const,
  commissions: ['commissions'] as const,
  milestones: ['milestones'] as const,
  rewards: ['rewards'] as const,
  payouts: ['payouts'] as const,
};

/**
 * Apply one realtime event to the query cache. Small events (driver positions) patch the cache in place; anything
 * else invalidates, so REST stays the source of truth (events are best-effort — docs/API.md §9).
 */
export function applyRealtime(qc: QueryClient, event: string, payload: unknown): void {
  switch (event) {
    case 'driver:location': {
      const p = payload as DriverLocationEvent;
      const loc = { lat: p.lat, lng: p.lng, heading: p.heading ?? null, at: p.at };
      let known = false;
      qc.setQueryData<OnlineDriver[]>(qk.liveDrivers, (list) =>
        list?.map((d) => {
          if (d.id !== p.driverId) return d;
          known = true;
          return { ...d, location: loc };
        }),
      );
      if (!known && qc.getQueryData(qk.liveDrivers)) void qc.invalidateQueries({ queryKey: qk.liveDrivers });
      if (p.rideId)
        qc.setQueryData<AdminRide[]>(qk.liveRides, (list) =>
          list?.map((r) => (r.id === p.rideId && r.driver ? { ...r, driver: { ...r.driver, location: loc } } : r)),
        );
      return;
    }
    case 'driver:updated': {
      const p = payload as DriverUpdatedEvent;
      if (p.isOnline === false) {
        qc.setQueryData<OnlineDriver[]>(qk.liveDrivers, (list) => list?.filter((d) => d.id !== p.id));
      } else if (p.isOnline === true && p.lat != null && p.lng != null) {
        qc.setQueryData<OnlineDriver[]>(qk.liveDrivers, (list) =>
          list?.map((d) =>
            d.id === p.id ? { ...d, location: { lat: p.lat!, lng: p.lng!, heading: null, at: new Date().toISOString() } } : d,
          ),
        );
      }
      void qc.invalidateQueries({ queryKey: qk.liveDrivers });
      void qc.invalidateQueries({ queryKey: qk.drivers });
      void qc.invalidateQueries({ queryKey: qk.driver(p.id) });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
      return;
    }
    case 'ride:updated': {
      const p = payload as RideUpdatedEvent;
      if (p.truncated || !p.id) {
        void qc.invalidateQueries({ queryKey: qk.liveRides });
      } else {
        const open = OPEN_STATUSES.includes(p.status);
        qc.setQueryData<AdminRide[]>(qk.liveRides, (list) => {
          if (!list) return list;
          const rest = list.filter((r) => r.id !== p.id);
          if (!open) return rest;
          const existing = list.find((r) => r.id === p.id);
          const next = p.ride ?? (existing ? { ...existing, status: p.status } : undefined);
          return next ? [...rest, next].sort((a, b) => a.requestedAt.localeCompare(b.requestedAt)) : list;
        });
        if (open && !p.ride && !qc.getQueryData<AdminRide[]>(qk.liveRides)?.some((r) => r.id === p.id))
          void qc.invalidateQueries({ queryKey: qk.liveRides });
        void qc.invalidateQueries({ queryKey: qk.ride(p.id) });
      }
      void qc.invalidateQueries({ queryKey: qk.rides });
      void qc.invalidateQueries({ queryKey: qk.liveDrivers });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
      if (p.status === 'COMPLETED') {
        void qc.invalidateQueries({ queryKey: qk.settlements });
        void qc.invalidateQueries({ queryKey: qk.payouts });
        void qc.invalidateQueries({ queryKey: qk.ratings });
      }
      return;
    }
    case 'payout:updated':
      void qc.invalidateQueries({ queryKey: qk.payouts });
      void qc.invalidateQueries({ queryKey: qk.settlements });
      return;
    case 'sos:new':
    case 'sos:updated':
      void qc.invalidateQueries({ queryKey: qk.sos });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
      return;
  }
}

export const REALTIME_EVENTS = ['driver:location', 'driver:updated', 'ride:updated', 'sos:new', 'sos:updated', 'payout:updated'] as const;
